/**
 *
 * Persist User ID Helper
 *
 * Manages user ID for redux-persist key namespacing. Stores user ID in localStorage separately from redux-persist to ensure persist keys are available at store creation time.
 *
 */
import { IS_DEV } from '~/lib/constants'

const PERSIST_USER_KEY = 'oylo:persist_user_id'

// List of all persisted slice names (must match store config)
const PERSISTED_SLICES = ['camera', 'input', 'key_bindings', 'outputMapping', 'playback', 'tutorial', 'ui', 'settings']

/**
 * Get the stored user ID for persist key namespacing
 * @returns {string|null} The stored user ID or null if not set
 */
export const getPersistedUserId = () => {
	try {
		return localStorage.getItem(PERSIST_USER_KEY)
	} catch {
		return null
	}
}

/**
 * Set the user ID for persist key namespacing
 * @param {string} userId - The user ID to store
 */
export const setPersistedUserId = userId => {
	try {
		localStorage.setItem(PERSIST_USER_KEY, userId)
	} catch {
		// localStorage might be unavailable
	}
}

/**
 * Clear the stored user ID (for logout)
 */
export const clearPersistedUserId = () => {
	try {
		localStorage.removeItem(PERSIST_USER_KEY)
	} catch {
		// localStorage might be unavailable
	}
}

/**
 * Generate a persist key with user namespace
 * @param {string} sliceName - The name of the slice
 * @returns {string} The namespaced persist key
 */
export const getPersistKey = sliceName => {
	const userId = getPersistedUserId()
	if (userId) {
		return `${sliceName}:${userId}`
	}
	return sliceName
}

/**
 * Clear old non-user-specific persist keys to prevent accidental reads Also clears legacy keys from before user-namespacing was implemented
 */
const clearLegacyPersistKeys = () => {
	try {
		// Clear non-namespaced keys (legacy keys without user ID)
		PERSISTED_SLICES.forEach(slice => {
			localStorage.removeItem(`persist:${slice}`)
			localStorage.removeItem(slice)
		})
	} catch {
		// localStorage might be unavailable
	}
}

/**
 * Check if user ID has changed and needs a reload
 * @param {string} newUserId - The new user ID from auth
 * @returns {boolean} True if user changed and reload is needed
 */
export const checkUserChanged = newUserId => {
	if (IS_DEV) console.log('checkUserChanged', newUserId)
	// Either no stored user yet (first login) or different user Clear legacy non-user-specific keys to prevent accidental reads
	clearLegacyPersistKeys()
	const currentUserId = getPersistedUserId()
	// If same user, no change needed
	if (currentUserId === newUserId) {
		return false
	}
	// Store the new ID and reload to get correct persist keys
	setPersistedUserId(newUserId)
	return true
}
